import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

/** Bank card (ISO/IEC 7810 ID-1), in mm */
const CARD_LONG_MM = 85.6;
const CARD_SHORT_MM = 53.98;
/** First guess before calibration: 96 CSS px per inch */
const DEFAULT_PX_PER_MM = 96 / 25.4;
const STORAGE_KEY = 'varmier-size-guide:px-per-mm';
/** A measure this close to a size (mm of circumference) is that size: display rounding */
const EXACT_TOLERANCE = 0.2;
/** Converter: a known FR / US / mm size this close to one of our sizes is that size (US 9 = 59,5 → 59) */
const EQUIVALENT_TOLERANCE = 0.5;
/** Up to this much under our smallest size, the smallest size still fits; below, out of range */
const BELOW_TOLERANCE = 0.5;

const formatMm = (/** @type {number} */ value) =>
  value.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const formatSize = (/** @type {number} */ value) => value.toLocaleString('fr-FR', { maximumFractionDigits: 1 });

/** Saved calibration, or null when storage is unavailable or empty */
function readCalibration() {
  try {
    const value = Number(localStorage.getItem(STORAGE_KEY));
    return value > 0 ? value : null;
  } catch {
    return null;
  }
}

/** @param {number} value */
function saveCalibration(value) {
  try {
    localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // Private browsing or blocked storage: the visitor calibrates again next time
  }
}

/**
 * Reads a number typed with a dot or a comma
 * @param {HTMLInputElement} input
 */
function inputNumber(input) {
  if (!Number.isNaN(input.valueAsNumber)) return input.valueAsNumber;
  const value = Number(input.value.replace(',', '.'));
  return input.value.trim() === '' || Number.isNaN(value) ? null : value;
}

/**
 * @typedef {{ size: number, available: boolean }} Size
 * @typedef {{ sizes: Size[], adjustable: boolean, catalogueMin: number, catalogueMax: number, us: [number, number][] }} SizeData
 * @typedef {{ circumference: number, mode: 'measure' | 'equivalent' }} Measure
 *
 * @typedef {object} Refs
 * @property {HTMLScriptElement} data
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 * @property {HTMLElement} calibrateStep
 * @property {HTMLElement} measureStep
 * @property {HTMLElement} cardStage
 * @property {HTMLElement} card
 * @property {HTMLElement} cardSide
 * @property {HTMLInputElement} cardRange
 * @property {HTMLElement} ringStage
 * @property {HTMLElement} ring
 * @property {HTMLInputElement} ringRange
 * @property {HTMLElement} ringReadout
 * @property {HTMLInputElement} stringInput
 * @property {HTMLInputElement[]} knownInputs
 * @property {HTMLElement} result
 *
 * @extends {Component<Refs>}
 */
class VarmierSizeGuide extends Component {
  /** @type {SizeData} */
  #data = { sizes: [], adjustable: false, catalogueMin: 0, catalogueMax: 0, us: [] };
  #tab = 'ring';
  #pxPerMm = DEFAULT_PX_PER_MM;
  #calibrated = false;
  /** Card long side on the screen, in CSS px, while calibrating */
  #cardPx = CARD_LONG_MM * DEFAULT_PX_PER_MM;
  /** @type {Record<string, Measure | null>} */
  #measures = { ring: null, string: null, known: null };
  /** Field message for the active tab (out of bounds input) */
  /** @type {Record<string, string>} */
  #messages = { ring: '', string: '', known: '' };
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {number | undefined} */
  #renderTimer;

  connectedCallback() {
    super.connectedCallback();
    try {
      this.#data = { ...this.#data, ...JSON.parse(this.refs.data?.textContent || '{}') };
    } catch {
      // Keep the empty data: the tool still measures, without a size recommendation
    }
    this.#data.sizes.sort((a, b) => a.size - b.size);

    const saved = readCalibration();
    if (saved) {
      this.#pxPerMm = saved;
      this.#calibrated = true;
    }
    this.#cardPx = CARD_LONG_MM * this.#pxPerMm;
    if (this.refs.cardRange) this.refs.cardRange.value = String(this.#cardPx);

    // The stages are measured when the tool becomes visible (drawer opening, rotation)
    this.#resizeObserver = new ResizeObserver(() => {
      this.#drawCard();
      this.#drawRing();
    });
    this.#resizeObserver.observe(this);
    this.refs.result.addEventListener('click', this.#handleResultClick);

    this.#showRingStep();
    this.#updateRing(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.refs.result?.removeEventListener('click', this.#handleResultClick);
    clearTimeout(this.#renderTimer);
  }

  /* ---------- Tabs ---------- */

  /** @param {Event} event */
  selectTab(event) {
    const tab = /** @type {HTMLElement} */ (event.target).closest('[data-tab]');
    if (tab instanceof HTMLElement) this.#activate(tab.dataset.tab ?? 'ring');
  }

  /** Arrow keys, Home and End move between the tabs */
  /** @param {KeyboardEvent} event */
  handleTabKey(event) {
    const tabs = this.refs.tabs;
    const index = tabs.findIndex((tab) => tab.dataset.tab === this.#tab);
    const next = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: tabs.length - 1 }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    const tab = tabs[(next + tabs.length) % tabs.length];
    if (!tab) return;
    this.#activate(tab.dataset.tab ?? 'ring');
    tab.focus();
  }

  /** @param {string} name */
  #activate(name) {
    this.#tab = name;
    for (const tab of this.refs.tabs) {
      const selected = tab.dataset.tab === name;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    }
    for (const panel of this.refs.panels) panel.hidden = panel.dataset.panel !== name;
    if (name === 'ring') {
      this.#drawCard();
      this.#drawRing();
    }
    this.#renderResult();
  }

  /* ---------- Tab 1: calibration, then the ring ---------- */

  /** @param {Event} event */
  handleCardRange(event) {
    this.#cardPx = Number(/** @type {HTMLInputElement} */ (event.target).value);
    this.#drawCard();
  }

  handleRingRange() {
    this.#updateRing(true);
  }

  /** −/+ buttons: fine steps (0,5 px for the card, 0,1 mm for the circle) */
  /** @param {Event} event */
  nudge(event) {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-target]');
    if (!(button instanceof HTMLElement)) return;
    const direction = Number(button.dataset.direction) || 1;
    const range = button.dataset.target === 'card' ? this.refs.cardRange : this.refs.ringRange;
    const step = Number(range.step) || 1;
    const value = Math.min(Number(range.max), Math.max(Number(range.min), Number(range.value) + direction * step));
    range.value = String(Math.round(value * 100) / 100);
    if (button.dataset.target === 'card') {
      this.#cardPx = Number(range.value);
      this.#drawCard();
    } else {
      this.#updateRing(true);
    }
  }

  confirmCalibration() {
    this.#pxPerMm = this.#cardPx / CARD_LONG_MM;
    this.#calibrated = true;
    saveCalibration(this.#pxPerMm);
    this.#showRingStep();
    this.#updateRing(false);
    this.refs.ringRange?.focus();
  }

  recalibrate() {
    this.#calibrated = false;
    this.#measures.ring = null;
    this.#cardPx = CARD_LONG_MM * this.#pxPerMm;
    this.refs.cardRange.value = String(this.#cardPx);
    this.#showRingStep();
    this.#renderResult();
    this.refs.cardRange?.focus();
  }

  #showRingStep() {
    this.refs.calibrateStep.hidden = this.#calibrated;
    this.refs.measureStep.hidden = !this.#calibrated;
    this.#drawCard();
    this.#drawRing();
  }

  /** The card is drawn on its long side; on a narrow screen it stands upright (« posée en hauteur ») */
  #drawCard() {
    const { card, cardStage, cardSide } = this.refs;
    if (!card || !cardStage || this.refs.calibrateStep.hidden) return;
    const available = cardStage.clientWidth - 24;
    if (available <= 0) return;
    const long = this.#cardPx;
    const short = (long * CARD_SHORT_MM) / CARD_LONG_MM;
    const upright = long > available;
    card.style.width = `${upright ? short : long}px`;
    card.style.height = `${upright ? long : short}px`;
    if (cardSide) cardSide.textContent = upright ? 'longueur (carte posée en hauteur)' : 'largeur';
  }

  #drawRing() {
    const { ring, ringStage, ringRange } = this.refs;
    if (!ring || !ringStage || this.refs.measureStep.hidden) return;
    const diameter = Number(ringRange.value);
    const px = diameter * this.#pxPerMm;
    ring.style.width = `${px}px`;
    ring.style.height = `${px}px`;
    // Room for the largest circle, so the stage does not jump while the circle grows
    ringStage.style.minHeight = `${Number(ringRange.max) * this.#pxPerMm + 32}px`;
  }

  /** @param {boolean} debounce */
  #updateRing(debounce) {
    if (!this.#calibrated) return;
    this.#drawRing();
    const diameter = Number(this.refs.ringRange.value);
    const circumference = diameter * Math.PI;
    this.#measures.ring = { circumference, mode: 'measure' };
    const recommendation = this.#recommend(this.#measures.ring);
    const estimate =
      recommendation.kind === 'size'
        ? String(recommendation.size)
        : recommendation.kind === 'adjustable'
          ? 'anneau ajustable'
          : 'hors de nos tailles';
    this.refs.ringReadout.textContent = `Diamètre ${formatMm(diameter)} mm · taille estimée : ${estimate}`;
    this.#renderResult(debounce);
  }

  /* ---------- Tab 2: string ---------- */

  /** @param {Event} event */
  handleString(event) {
    const value = inputNumber(/** @type {HTMLInputElement} */ (event.target));
    this.#messages.string = '';
    this.#measures.string = null;
    if (value !== null) {
      if (value < 40 || value > 80) this.#messages.string = 'Indiquez un tour de doigt entre 40 et 80 mm.';
      else this.#measures.string = { circumference: value, mode: 'measure' };
    }
    this.#renderResult(true);
  }

  /* ---------- Tab 3: known size, FR / US / diameter ---------- */

  /** @param {Event} event */
  handleKnown(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const value = inputNumber(input);
    this.#messages.known = '';
    this.#measures.known = null;

    let circumference = null;
    if (value !== null && value > 0) {
      if (input.dataset.unit === 'fr') circumference = value;
      if (input.dataset.unit === 'mm') circumference = value * Math.PI;
      if (input.dataset.unit === 'us') {
        circumference = this.#circumferenceFromUs(value);
        if (circumference === null) {
          const [first, last] = [this.#data.us[0]?.[0], this.#data.us.at(-1)?.[0]];
          this.#messages.known = `Notre tableau va de la taille US ${formatSize(first ?? 0)} à ${formatSize(last ?? 0)}.`;
        }
      }
    }

    for (const other of this.refs.knownInputs) {
      if (other === input) continue;
      if (circumference === null) {
        other.value = '';
        continue;
      }
      if (other.dataset.unit === 'fr') other.value = String(Math.round(circumference * 10) / 10);
      if (other.dataset.unit === 'mm') other.value = String(Math.round((circumference / Math.PI) * 10) / 10);
      if (other.dataset.unit === 'us') {
        const us = this.#usFromCircumference(circumference);
        other.value = us === null ? '' : String(us);
      }
    }

    if (circumference !== null) this.#measures.known = { circumference, mode: 'equivalent' };
    this.#renderResult(true);
  }

  /* ---------- Sizes ---------- */

  /**
   * US size for a circumference, by linear interpolation in the table, to the nearest half size
   * @param {number} circumference
   */
  #usFromCircumference(circumference) {
    const points = this.#data.us;
    for (let i = 0; i < points.length - 1; i++) {
      const [usA, mmA] = /** @type {[number, number]} */ (points[i]);
      const [usB, mmB] = /** @type {[number, number]} */ (points[i + 1]);
      if (circumference >= mmA && circumference <= mmB) {
        const us = usA + ((circumference - mmA) / (mmB - mmA)) * (usB - usA);
        return Math.round(us * 2) / 2;
      }
    }
    return null;
  }

  /**
   * Circumference for a US size, by linear interpolation in the table
   * @param {number} us
   */
  #circumferenceFromUs(us) {
    const points = this.#data.us;
    for (let i = 0; i < points.length - 1; i++) {
      const [usA, mmA] = /** @type {[number, number]} */ (points[i]);
      const [usB, mmB] = /** @type {[number, number]} */ (points[i + 1]);
      if (us >= usA && us <= usB) return mmA + ((us - usA) / (usB - usA)) * (mmB - mmA);
    }
    return null;
  }

  /**
   * @param {Measure} measure
   * @returns {{ kind: 'adjustable' } | { kind: 'below' | 'above' } | { kind: 'size', size: number, available: boolean, roundedUp: boolean, smallest: boolean }}
   */
  #recommend({ circumference, mode }) {
    const { sizes, adjustable, catalogueMin } = this.#data;
    if (adjustable) return { kind: 'adjustable' };
    if (!sizes.length) return { kind: 'above' };
    if (circumference < catalogueMin - BELOW_TOLERANCE) return { kind: 'below' };

    const pick = (/** @type {Size} */ entry, roundedUp = false) => ({
      kind: /** @type {const} */ ('size'),
      size: entry.size,
      available: entry.available,
      roundedUp,
      smallest: entry === sizes[0] && entry.size > catalogueMin && circumference < entry.size - EQUIVALENT_TOLERANCE,
    });

    const tolerance = mode === 'equivalent' ? EQUIVALENT_TOLERANCE : EXACT_TOLERANCE;
    const match = sizes.find((entry) => Math.abs(entry.size - circumference) <= tolerance);
    if (match) return pick(match);

    // Between two sizes, or just under one: the size above
    const above = sizes.find((entry) => entry.size >= circumference);
    if (above) return pick(above, true);
    return { kind: 'above' };
  }

  /* ---------- Result ---------- */

  /** @param {boolean} [debounce] - Slider and typing: one announcement once the value settles */
  #renderResult(debounce = false) {
    clearTimeout(this.#renderTimer);
    if (debounce) {
      this.#renderTimer = window.setTimeout(() => this.#renderResult(false), 250);
      return;
    }

    const { result } = this.refs;
    const measure = this.#measures[this.#tab];
    const message = this.#messages[this.#tab];
    result.replaceChildren();

    if (!measure) {
      if (message) result.append(this.#paragraph('vsg-result__message', message));
      return;
    }

    const { circumference } = measure;
    const measureLine = this.#paragraph(
      'vsg-result__measure',
      `Diamètre ${formatMm(circumference / Math.PI)} mm · tour de doigt ${formatMm(circumference)} mm`
    );
    const recommendation = this.#recommend(measure);

    if (recommendation.kind === 'adjustable') {
      result.append(this.#paragraph('vsg-result__size', 'Anneau ajustable : il s\'adapte à votre doigt'), measureLine);
      return;
    }

    if (recommendation.kind === 'below' || recommendation.kind === 'above') {
      const range = `${this.#data.catalogueMin} à ${this.#data.catalogueMax}`;
      const where = recommendation.kind === 'below' ? 'en dessous' : 'au-dessus';
      const contact = document.createElement('a');
      contact.className = 'vsg-link';
      contact.href = this.dataset.contactUrl ?? '/pages/contact';
      contact.textContent = 'Nous contacter';
      result.append(
        this.#paragraph('vsg-result__message', `Votre mesure est ${where} de nos tailles disponibles (${range}).`),
        measureLine,
        contact
      );
      return;
    }

    const title = document.createElement('p');
    title.className = 'vsg-result__size';
    const strong = document.createElement('strong');
    strong.textContent = String(recommendation.size);
    title.append('Votre taille : ', strong);
    result.append(title, measureLine, this.#paragraph('vsg-result__small', 'Mesure indicative, à une taille près.'));

    if (recommendation.smallest) {
      result.append(
        this.#paragraph('vsg-result__advice', `${recommendation.size} est la plus petite taille disponible pour ce modèle.`)
      );
    } else if (recommendation.roundedUp) {
      result.append(
        this.#paragraph(
          'vsg-result__advice',
          'Une chevalière est large, elle serre plus qu\'un anneau fin : nous vous conseillons la taille au-dessus.'
        )
      );
    }

    if (this.dataset.context === 'product') {
      if (recommendation.available) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'vsg-button';
        button.dataset.chooseSize = String(recommendation.size);
        button.textContent = `Choisir la taille ${recommendation.size}`;
        result.append(button);
      } else {
        result.append(
          this.#paragraph('vsg-result__message', `La taille ${recommendation.size} est en rupture de stock sur ce modèle.`)
        );
      }
    } else {
      const link = document.createElement('a');
      link.className = 'vsg-button';
      link.href = this.dataset.collectionUrl ?? '/collections/chevaliere-homme-argent';
      link.textContent = 'Découvrir les chevalières';
      result.append(link);
    }
  }

  /**
   * @param {string} className
   * @param {string} text
   */
  #paragraph(className, text) {
    const paragraph = document.createElement('p');
    paragraph.className = className;
    paragraph.textContent = text;
    return paragraph;
  }

  /** « Choisir la taille 62 »: the same as the customer's click in the size picker of the page */
  /** @param {MouseEvent} event */
  #handleResultClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-choose-size]');
    if (!(button instanceof HTMLElement)) return;
    const size = button.dataset.chooseSize;
    const input = document.querySelector(
      `#MainContent variant-picker input[type="radio"][data-option-name="Taille"][value="${size}"]`
    );
    if (!(input instanceof HTMLInputElement)) return;
    if (input.dataset.optionAvailable === 'false') {
      button.replaceWith(this.#paragraph('vsg-result__message', `La taille ${size} est en rupture de stock sur ce modèle.`));
      return;
    }
    if (!input.checked) input.click();
    this.dispatchEvent(new CustomEvent('varmier-size-guide:chosen', { bubbles: true, detail: { size } }));
  };
}

/**
 * Product page: « Trouver ma taille » opens the size guide in a modal dialog (a drawer on mobile).
 *
 * @typedef {object} ModalRefs
 * @property {HTMLDialogElement} dialog
 *
 * @extends {Component<ModalRefs>}
 */
class VarmierSizeGuideModal extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('close', this.#handleClose);
    this.refs.dialog.addEventListener('click', this.#handleBackdropClick);
    this.addEventListener('varmier-size-guide:chosen', this.close);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog?.removeEventListener('close', this.#handleClose);
    this.refs.dialog?.removeEventListener('click', this.#handleBackdropClick);
    this.removeEventListener('varmier-size-guide:chosen', this.close);
    if (this.refs.dialog) unlockScroll(this.refs.dialog);
  }

  open() {
    const { dialog } = this.refs;
    if (dialog.open) return;
    dialog.showModal();
    lockScroll(dialog);
  }

  close = () => {
    if (this.refs.dialog.open) this.refs.dialog.close();
  };

  #handleClose = () => {
    unlockScroll(this.refs.dialog);
    // Choosing a size re-renders the product details once the variant is loaded: give the focus back to
    // the link if it was lost meanwhile
    for (const delay of [0, 400, 900, 1500]) {
      window.setTimeout(() => {
        const active = document.activeElement;
        if (active && active !== document.body) return;
        const trigger = document.querySelector('#MainContent .vsg-trigger');
        if (trigger instanceof HTMLElement) trigger.focus();
      }, delay);
    }
  };

  /** A click on the backdrop (outside the panel) closes the dialog */
  /** @param {MouseEvent} event */
  #handleBackdropClick = (event) => {
    if (event.target === this.refs.dialog) this.close();
  };
}

if (!customElements.get('varmier-size-guide')) customElements.define('varmier-size-guide', VarmierSizeGuide);
if (!customElements.get('varmier-size-guide-modal')) {
  customElements.define('varmier-size-guide-modal', VarmierSizeGuideModal);
}
